"use client";

import { FAQAccordion } from "@/components/FAQAccordion";
import { PageBottomCta } from "@/components/PageBottomCta";
import { useLanguage } from "@/components/LanguageProvider";
import type { FAQ } from "@/lib/types";

type Props = {
  faqs: FAQ[];
};

export function AboutContent({ faqs }: Props) {
  const { t, dir } = useLanguage();

  return (
    <div className="flex flex-col bg-gradient-to-b from-white to-slate-50" dir={dir}>
      <header className="border-b border-slate-200/80 bg-gradient-to-br from-white via-slate-50 to-[#f0f9ff] px-4 py-14 text-center md:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--accent)]">
          {t("about.badge")}
        </p>
        <h1 className="mt-3 text-balance text-4xl font-extrabold tracking-tight text-[var(--primary)] md:text-5xl">
          {t("about.title")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-pretty text-slate-600 md:text-lg">
          {t("about.subtitle")}
        </p>
      </header>

      <div className="mx-auto w-full max-w-4xl space-y-8 px-4 py-14 md:px-6 md:py-[var(--section-space)]">
        <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
          <h2 className="text-xl font-bold text-[var(--primary)]">{t("about.mission")}</h2>
          <p className="mt-4 text-pretty leading-relaxed text-slate-600">
            {t("about.mission.p1")}
          </p>
          <p className="mt-4 text-pretty leading-relaxed text-slate-600">
            {t("about.mission.p2")}
          </p>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm md:p-10">
          <h2 className="text-xl font-bold text-[var(--primary)]">{t("about.values")}</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {(
              [
                {
                  title: t("about.values.resilience.title"),
                  desc: t("about.values.resilience.desc"),
                },
                {
                  title: t("about.values.innovation.title"),
                  desc: t("about.values.innovation.desc"),
                },
                {
                  title: t("about.values.client.title"),
                  desc: t("about.values.client.desc"),
                },
              ] as { title: string; desc: string }[]
            ).map((x) => (
              <div key={x.title}>
                <h3 className="font-semibold text-[var(--primary)]">{x.title}</h3>
                <p className="mt-2 text-pretty text-sm leading-relaxed text-slate-600">
                  {x.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <div className="grid gap-8 md:grid-cols-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-lg font-bold text-[var(--primary)]">
              {t("about.history")}
            </h2>
            <p className="mt-4 text-pretty text-sm leading-relaxed text-slate-600">
              {t("about.history.p1")}
            </p>
          </section>
          <section className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <h2 className="text-lg font-bold text-[var(--primary)]">{t("about.team")}</h2>
            <p className="mt-4 text-pretty text-sm leading-relaxed text-slate-600">
              {t("about.team.p1")}
            </p>
          </section>
        </div>
      </div>

      <PageBottomCta
        fallback={{
          heading: "home.cta.heading",
          subheading: "home.cta.subtitle",
          button: "home.cta.button",
        }}
      />

      {faqs.length > 0 ? (
        <FAQAccordion
          heading={t("about.faq.heading")}
          subheading={t("about.faq.subheading")}
          faqs={faqs}
          className="border-t border-slate-200/80 bg-white"
        />
      ) : null}
    </div>
  );
}
